import { Reducer } from "redux";

import {
	CustomField,
	Supplier,
	IsFormEdited
} from "@/Contracts/Settings";
import { CurrencyConfigurationView } from "@/Contracts";

import { SettingsActions, SettingsActionTypes } from "@/Actions/Settings";
import { CURRENCY_CONFIG_ACTIONS } from "@/Actions/Settings/currencyConfig";

export interface SettingsState {
	readonly isLoading: boolean;
	readonly appUpdateBannerVisible: boolean;
	readonly customFieldList: CustomField[];
	readonly connectWiseDrawerOpen: boolean;
	readonly zapierDrawerOpen: boolean;
	readonly kaseyaDrawerOpen: boolean;
	readonly accountSettingsDrawerOpen: boolean;
	readonly accountPasswordDrawerOpen: boolean;
	readonly addCompanyUserDrawerOpen: boolean;
	readonly editCompanyUserDrawerOpen: boolean;
	readonly companyLogoModalOpen: boolean;
	readonly ingramDrawerOpen: boolean;
	readonly techDataDrawerOpen: boolean;
	readonly dAndHDrawerOpen: boolean;
	readonly synnexDrawerOpen: boolean;
	readonly connectBoosterDrawerOpen: boolean;
	readonly addCustomFieldDrawerOpen: boolean;
	readonly editCustomFieldDrawerOpen: boolean;
	readonly commissionsDrawerOpen: boolean;
	readonly dickerDataDrawerOpen: boolean;
	readonly westcoastDrawerOpen: boolean;
	readonly suppliers: Supplier[];
	readonly currentActiveSupplier?: Supplier;
	readonly addOrEditSupplierDrawerOpen: boolean;
	readonly isFormEdited: IsFormEdited | null;
	readonly isFetchingPDF: boolean;
	readonly currencyConfig: CurrencyConfigurationView | null;
	readonly currencyConfigLoading: boolean;
	readonly currencyConfigError: string | null;
}

export const initialSettingsState: SettingsState = {
	isLoading: false,
	appUpdateBannerVisible: false,
	customFieldList: new Array<CustomField>(),
	connectWiseDrawerOpen: false,
	zapierDrawerOpen: false,
	kaseyaDrawerOpen: false,
	accountSettingsDrawerOpen: false,
	accountPasswordDrawerOpen: false,
	addCompanyUserDrawerOpen: false,
	editCompanyUserDrawerOpen: false,
	companyLogoModalOpen: false,
	ingramDrawerOpen: false,
	techDataDrawerOpen: false,
	dAndHDrawerOpen: false,
	synnexDrawerOpen: false,
	connectBoosterDrawerOpen: false,
	addCustomFieldDrawerOpen: false,
	editCustomFieldDrawerOpen: false,
	commissionsDrawerOpen: false,
	dickerDataDrawerOpen: false,
	westcoastDrawerOpen: false,
	suppliers: new Array<Supplier>(),
	addOrEditSupplierDrawerOpen: false,
	isFormEdited: null,
	isFetchingPDF: false,
	currencyConfig: null,
	currencyConfigLoading: false,
	currencyConfigError: null
};

export const SettingsReducer: Reducer<SettingsState, SettingsActions> = (
	state: SettingsState = initialSettingsState,
	action: SettingsActions
): SettingsState => {
	switch (action.type) {
		case SettingsActionTypes.SHOW_SETTINGS_LOADER: {
			return {
				...state,
				isLoading: true
			};
		}
		case SettingsActionTypes.HIDE_SETTINGS_LOADER: {
			return {
				...state,
				isLoading: false
			};
		}
		case SettingsActionTypes.SHOW_APP_UPDATE_BANNER: {
			return {
				...state,
				appUpdateBannerVisible: true
			};
		}
		case SettingsActionTypes.HIDE_APP_UPDATE_BANNER: {
			return {
				...state,
				appUpdateBannerVisible: false
			};
		}
		case SettingsActionTypes.ADD_CUSTOM_FIELD_LIST_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.ADD_CUSTOM_FIELD_LIST_SUCCESSFUL: {
			return {
				...state,
				customFieldList: [action.addCustomField].concat(
					state.customFieldList
				)
			};
		}
		case SettingsActionTypes.ADD_CUSTOM_FIELD_LIST_FAILED: {
			return state;
		}
		case SettingsActionTypes.EDIT_CUSTOM_FIELD_LIST_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.EDIT_CUSTOM_FIELD_LIST_SUCCESSFUL: {
			const customFieldList = state.customFieldList.filter(
				customField => customField.id !== action.editCustomField.id
			);
			const updateCustomFieldIndex = state.customFieldList.findIndex(
				customField => customField.id === action.editCustomField.id
			);
			if (updateCustomFieldIndex > -1) {
				customFieldList.splice(
					updateCustomFieldIndex,
					0,
					action.editCustomField
				);
			}
			return {
				...state,
				customFieldList
			};
		}
		case SettingsActionTypes.EDIT_CUSTOM_FIELD_LIST_FAILED: {
			return state;
		}
		case SettingsActionTypes.CONNECTWISE_DRAWER_OPENED: {
			return {
				...state,
				connectWiseDrawerOpen: true
			};
		}
		case SettingsActionTypes.CONNECTWISE_DRAWER_CLOSED: {
			return {
				...state,
				connectWiseDrawerOpen: false
			};
		}
		case SettingsActionTypes.KASEYA_DRAWER_OPENED: {
			return {
				...state,
				kaseyaDrawerOpen: true
			};
		}
		case SettingsActionTypes.KASEYA_DRAWER_CLOSED: {
			return {
				...state,
				kaseyaDrawerOpen: false
			};
		}
		case SettingsActionTypes.ZAPIER_DRAWER_OPENED: {
			return {
				...state,
				zapierDrawerOpen: true
			};
		}
		case SettingsActionTypes.ZAPIER_DRAWER_CLOSED: {
			return {
				...state,
				zapierDrawerOpen: false
			};
		}
		case SettingsActionTypes.ACCOUNT_SETTINGS_DRAWER_OPENED: {
			return {
				...state,
				accountSettingsDrawerOpen: true
			};
		}
		case SettingsActionTypes.ACCOUNT_SETTINGS_DRAWER_CLOSED: {
			return {
				...state,
				accountSettingsDrawerOpen: false
			};
		}
		case SettingsActionTypes.ADD_COMPANY_USER_DRAWER_OPENED: {
			return {
				...state,
				addCompanyUserDrawerOpen: true
			};
		}
		case SettingsActionTypes.ADD_COMPANY_USER_DRAWER_CLOSED: {
			return {
				...state,
				addCompanyUserDrawerOpen: false
			};
		}
		case SettingsActionTypes.EDIT_COMPANY_USER_DRAWER_OPENED: {
			return {
				...state,
				editCompanyUserDrawerOpen: true
			};
		}
		case SettingsActionTypes.EDIT_COMPANY_USER_DRAWER_CLOSED: {
			return {
				...state,
				editCompanyUserDrawerOpen: false
			};
		}
		case SettingsActionTypes.COMPANY_LOGO_MODAL_OPENED: {
			return {
				...state,
				companyLogoModalOpen: true
			};
		}
		case SettingsActionTypes.COMPANY_LOGO_MODAL_CLOSED: {
			return {
				...state,
				companyLogoModalOpen: false
			};
		}
		case SettingsActionTypes.INGRAM_DRAWER_OPENED: {
			return {
				...state,
				ingramDrawerOpen: true
			};
		}
		case SettingsActionTypes.INGRAM_DRAWER_CLOSED: {
			return {
				...state,
				ingramDrawerOpen: false
			};
		}
		case SettingsActionTypes.TECH_DATA_DRAWER_OPENED: {
			return {
				...state,
				techDataDrawerOpen: true
			};
		}
		case SettingsActionTypes.TECH_DATA_DRAWER_CLOSED: {
			return {
				...state,
				techDataDrawerOpen: false
			};
		}
		case SettingsActionTypes.D_AND_H_DRAWER_OPENED: {
			return {
				...state,
				dAndHDrawerOpen: true
			};
		}
		case SettingsActionTypes.D_AND_H_DRAWER_CLOSED: {
			return {
				...state,
				dAndHDrawerOpen: false
			};
		}
		case SettingsActionTypes.SYNNEX_DRAWER_OPENED: {
			return {
				...state,
				synnexDrawerOpen: true
			};
		}
		case SettingsActionTypes.SYNNEX_DRAWER_CLOSED: {
			return {
				...state,
				synnexDrawerOpen: false
			};
		}
		case SettingsActionTypes.CB_DRAWER_OPENED: {
			return {
				...state,
				connectBoosterDrawerOpen: true
			};
		}
		case SettingsActionTypes.CB_DRAWER_CLOSED: {
			return {
				...state,
				connectBoosterDrawerOpen: false
			};
		}
		case SettingsActionTypes.ADD_CUSTOM_FIELD_DRAWER_OPENED: {
			return {
				...state,
				addCustomFieldDrawerOpen: true
			};
		}
		case SettingsActionTypes.ADD_CUSTOM_FIELD_DRAWER_CLOSED: {
			return {
				...state,
				addCustomFieldDrawerOpen: false
			};
		}
		case SettingsActionTypes.EDIT_CUSTOM_FIELD_DRAWER_OPENED: {
			return {
				...state,
				editCustomFieldDrawerOpen: true
			};
		}
		case SettingsActionTypes.EDIT_CUSTOM_FIELD_DRAWER_CLOSED: {
			return {
				...state,
				editCustomFieldDrawerOpen: false
			};
		}
		case SettingsActionTypes.COMMISSIONS_DRAWER_OPENED: {
			return {
				...state,
				commissionsDrawerOpen: true
			};
		}
		case SettingsActionTypes.COMMISSIONS_DRAWER_CLOSED: {
			return {
				...state,
				commissionsDrawerOpen: false
			};
		}
		case SettingsActionTypes.TEST_CONNECTWISE_CREDENTIALS_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.TEST_CONNECTWISE_CREDENTIALS_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.TEST_CONNECTWISE_CREDENTIALS_FAILED: {
			return state;
		}
		case SettingsActionTypes.TEST_SYNCRO_CREDENTIALS_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.TEST_SYNCRO_CREDENTIALS_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.TEST_SYNCRO_CREDENTIALS_FAILED: {
			return state;
		}
		case SettingsActionTypes.TEST_KASEYA_CREDENTIALS_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.TEST_KASEYA_CREDENTIALS_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.TEST_KASEYA_CREDENTIALS_FAILED: {
			return state;
		}
		case SettingsActionTypes.CONNECTWISE_TAX_IMPORT_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.CONNECTWISE_TAX_IMPORT_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.CONNECTWISE_TAX_IMPORT_FAILED: {
			return state;
		}
		case SettingsActionTypes.AUTOTASK_TAX_IMPORT_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.AUTOTASK_TAX_IMPORT_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.AUTOTASK_TAX_IMPORT_FAILED: {
			return state;
		}
		case SettingsActionTypes.SHOW_DICKER_DATA_DRAWER: {
			return {
				...state,
				dickerDataDrawerOpen: true
			};
		}
		case SettingsActionTypes.HIDE_DICKER_DATA_DRAWER: {
			return {
				...state,
				dickerDataDrawerOpen: false
			};
		}
		case SettingsActionTypes.SHOW_WESTCOAST_DRAWER: {
			return {
				...state,
				westcoastDrawerOpen: true
			};
		}
		case SettingsActionTypes.HIDE_WESTCOAST_DRAWER: {
			return {
				...state,
				westcoastDrawerOpen: false
			};
		}
		case SettingsActionTypes.AUTOTASK_STAGE_MAPPING_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.AUTOTASK_STAGE_MAPPING_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.AUTOTASK_STAGE_MAPPING_FAILED: {
			return state;
		}
		case SettingsActionTypes.UPDATED_SYNC_DATA_REQUESTED: {
			return state;
		}
		case SettingsActionTypes.UPDATED_SYNC_DATA_SUCCESSFUL: {
			return state;
		}
		case SettingsActionTypes.UPDATED_SYNC_DATA_FAILED: {
			return state;
		}
		case SettingsActionTypes.GET_SUPPLIER_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveSupplier: action.supplierDetails
			};
		}
		case SettingsActionTypes.GET_SUPPLIER_SUCCESSFUL: {
			return {
				...state,
				suppliers: action.suppliers.data
			};
		}
		case SettingsActionTypes.SHOW_ADD_OR_EDIT_SUPPLIER_DRAWER: {
			return {
				...state,
				addOrEditSupplierDrawerOpen: true
			};
		}
		case SettingsActionTypes.HIDE_ADD_OR_EDIT_SUPPLIER_DRAWER: {
			return {
				...state,
				addOrEditSupplierDrawerOpen: false
			};
		}
		case SettingsActionTypes.EDIT_FORM: {
			return {
				...state,
				isFormEdited: {
					...state.isFormEdited,
					[action.formKey]: true
				}
			};
		}
		case SettingsActionTypes.EDIT_FORM_RESET: {
			return {
				...state,
				isFormEdited: {
					...state.isFormEdited,
					[action.formKey]: false
				}
			};
		}
		case SettingsActionTypes.FETCHING_PDF: {
			return {
				...state,
				isFetchingPDF: true
			};
		}
		case SettingsActionTypes.RESET_FETCHING_PDF: {
			return {
				...state,
				isFetchingPDF: false
			};
		}
		case CURRENCY_CONFIG_ACTIONS.GET_REQUEST:
			return {
				...state,
				currencyConfigLoading: true,
				currencyConfigError: null
			};
		case CURRENCY_CONFIG_ACTIONS.GET_SUCCESS:
		case CURRENCY_CONFIG_ACTIONS.ADD_SUCCESS:
		case CURRENCY_CONFIG_ACTIONS.UPDATE_SUCCESS:
			return {
				...state,
				currencyConfig: action.payload,
				currencyConfigLoading: false,
				currencyConfigError: null
			};
		case CURRENCY_CONFIG_ACTIONS.GET_FAIL:
		case CURRENCY_CONFIG_ACTIONS.ADD_FAIL:
		case CURRENCY_CONFIG_ACTIONS.UPDATE_FAIL:
		case CURRENCY_CONFIG_ACTIONS.DELETE_FAIL:
			return {
				...state,
				currencyConfigLoading: false,
				currencyConfigError: action.error ?? null
			};
		case CURRENCY_CONFIG_ACTIONS.DELETE_SUCCESS: {
			const removed = action.payload.currency;
			if (!state.currencyConfig) return state;
			return {
				...state,
				currencyConfig: {
					...state.currencyConfig,
					currencyConfigs: state.currencyConfig.currencyConfigs.filter(
						(c) => c.currency.code !== removed
					)
				},
				currencyConfigLoading: false,
				currencyConfigError: null
			};
		}
		default: {
			return state;
		}
	}
};

export default SettingsReducer;
